import { Box, SimpleGrid } from '@chakra-ui/react';
import React from 'react';

import {
  WLAccordion,
  WLAccordionHeader,
  WLAccordionItem,
  WLAccordionPanel,
  WLDivider,
  WLH1,
  WLH2,
  WLH3,
  WLH4,
  WLH5,
  WLH6,
  WLHeadlineDivider,
  WLSubjectBlock,
} from '../../design-library';
import { DividerColorScheme } from '../../design-library/Divider';

type HeadingType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
const HeadingMap: Record<string, HeadingType> = {
  WLH1: 'h1',
  WLH2: 'h2',
  WLH3: 'h3',
  WLH4: 'h4',
  WLH5: 'h5',
  WLH6: 'h6',
};

const isActiveBlock = (
  subject: string | React.ReactElement | null,
  members: React.ReactNode[],
) => !!subject || members.length > 0;
const buildBlock = (
  subject: string | null,
  members: React.ReactNode[],
  index: number,
) => (
  <WLSubjectBlock subject={subject || ''} key={`subjectBlock-${index}`}>
    {members}
  </WLSubjectBlock>
);
const buildGrid = (items: React.ReactElement[], index: number) => {
  if (!items || items.length === 0) {
    return null;
  }
  return (
    <SimpleGrid
      key={`grid-${index}`}
      columns={{ base: 1, md: 2 }}
      spacing={12}
      paddingY={4}
    >
      {items}
    </SimpleGrid>
  );
};
const buildGridItem = (members: React.ReactElement[], index: number) => {
  if (!members || members.length === 0) {
    return null;
  }
  return <Box key={`gridItem-${index}`}>{members}</Box>;
};
const buildHeadlineDivider = (
  element: React.ReactElement,
  level: HeadingType,
  index: number,
) => (
  <WLHeadlineDivider as={level} key={`heading-${index}`}>
    {extractTextContent(element)}
  </WLHeadlineDivider>
);
const buildDividerHeading = (
  element: React.ReactElement,
  level: HeadingType,
  index: number,
) => (
  <WLDivider
    as={level}
    key={`heading-${index}`}
    colorScheme={DividerColorScheme.MIXED}
  >
    {extractTextContent(element)}
  </WLDivider>
);
const buildAccordionItem = (
  heading: React.ReactElement,
  body: React.ReactElement[],
  index: number,
) => (
  <WLAccordionItem key={index}>
    <WLAccordionHeader>{heading}</WLAccordionHeader>
    <WLAccordionPanel>{body}</WLAccordionPanel>
  </WLAccordionItem>
);
const extractTextContent = (element: React.ReactElement): string =>
  element.props.children[0] as string;

/**
 * Attempt to reconstruct the list of children into a series of SubjectBlock components if MODE2 is active
 * This will output H1 & H2 as HeadlineDividers, while H3 indicates a new subject block and any subsequent elements will
 * be grouped into the SubjectBlock's content until the next H1, H2, or H3 is reached.
 */
export const Mode2Grouper = (
  children: React.ReactElement[],
): React.ReactElement[] => {
  let subject: string | null = null;
  let members: React.ReactNode[] = [];
  return children.reduce((acc, child, index) => {
    if (child) {
      if (
        isActiveBlock(subject, members) &&
        (child.type === WLH1 || child.type === WLH2 || child.type === WLH3)
      ) {
        acc.push(buildBlock(subject, members, index));
        subject = null;
        members = [];
      }
      if (child.type === WLH1 || child.type === WLH2) {
        acc.push(
          buildHeadlineDivider(child, HeadingMap[child.type.name], index),
        );
      } else if (child.type === WLH3) {
        subject = extractTextContent(child);
      } else if (isActiveBlock(subject, members)) {
        members.push(child);
      } else {
        acc.push(child);
      }
    }
    if (isActiveBlock(subject, members) && index === children.length - 1) {
      acc.push(buildBlock(subject, members, index));
    }
    return acc;
  }, [] as React.ReactElement[]);
};

/**
 * Attempt to reconstruct the list of children into a series of DividerHeadings with child grids if MODE3 is active
 * This will output H1 & H2 as HeadlineDividers, while H3 indicates a new grid item and any subsequent elements will
 * be grouped into the same grid item until the next H1, H2, or H3 is reached.
 */
export const Mode3Grouper = (
  children: React.ReactElement[],
): React.ReactElement[] => {
  let gridItems: React.ReactElement[] = [];
  let itemMembers: React.ReactElement[] = [];
  return children.reduce((acc, child, index) => {
    if (child) {
      if (
        child.type === WLH1 ||
        child.type === WLH2 ||
        child.type === WLDivider
      ) {
        const gridItem = buildGridItem(itemMembers, index);
        if (gridItem) {
          gridItems.push(gridItem);
          itemMembers = [];
        }
        const grid = buildGrid(gridItems, index);
        if (grid) {
          acc.push(grid);
        }
        gridItems = [];
        if (child.type === WLH1) {
          acc.push(child);
        } else if (child.type === WLH2) {
          acc.push(
            buildDividerHeading(child, HeadingMap[child.type.name], index),
          );
        } else {
          // Skip dividers, we only use them to break out of grid blocks
        }
      } else if (child.type === WLH3) {
        const gridItem = buildGridItem(itemMembers, index);
        if (gridItem) {
          gridItems.push(gridItem);
        }
        itemMembers = [child];
      } else {
        if (itemMembers && itemMembers.length > 0) {
          itemMembers.push(child);
        } else {
          acc.push(child);
        }
      }
    }
    if (index === children.length - 1) {
      const gridItem = buildGridItem(itemMembers, index);
      if (gridItem) {
        gridItems.push(gridItem);
      }
      const grid = buildGrid(gridItems, index);
      if (grid) {
        acc.push(grid);
      }
    }
    return acc;
  }, [] as React.ReactElement[]);
};

export const AccordionGrouper = (
  children: React.ReactElement[],
): React.ReactElement => {
  let heading: React.ReactElement | null = null;
  let members: React.ReactElement[] = [];
  const items = children.reduce((acc, child, index) => {
    if (child) {
      if (
        child.type === WLH1 ||
        child.type === WLH2 ||
        child.type === WLH3 ||
        child.type === WLH4 ||
        child.type === WLH5 ||
        child.type === WLH6
      ) {
        if (isActiveBlock(heading, members)) {
          acc.push(
            buildAccordionItem(heading as React.ReactElement, members, index),
          );
          heading = null;
          members = [];
        }
        if (child.type === WLH1 || child.type === WLH2 || child.type === WLH3) {
          acc.push(child);
        } else if (child.type === WLH4) {
          const text = extractTextContent(child);
          acc.push(
            <WLDivider
              key={`header-${index}`}
              as={HeadingMap[child.type.name]}
              colorScheme={DividerColorScheme.MIXED}
            >
              {text}
            </WLDivider>,
          );
        } else {
          heading = child;
        }
      } else {
        members.push(child);
      }
    }
    if (isActiveBlock(heading, members) && index === children.length - 1) {
      acc.push(
        buildAccordionItem(heading as React.ReactElement, members, index),
      );
    }
    return acc;
  }, [] as React.ReactElement[]);

  return (
    <WLAccordion allowToggle allowMultiple>
      {items}
    </WLAccordion>
  );
};
